import type {
  CheckoutTopology,
  CheckoutTopologyPreference,
  PaymentProviderName,
  ShippingMethod,
  ShippingProviderConfig
} from 'ecosystem'
import { hasAddressDependentConditions } from './shippingRules'

/**
 * What each payment provider's own checkout can do with shipping, for the
 * DEFAULT shipping branch (no third-party carrier widget).
 *
 * `nativeOptions` — the widget can render a list of shipping options we push
 * into the session, *and* tell us which one the shopper picked. Klarna/Kustom
 * qualifies (`shipping_options` on the order payload plus the
 * `shipping_option_change` event). Qliro takes a list too
 * (`AvailableShippingMethods`), but the `q1` listener set has no
 * shipping-method callback, so a choice made inside its iframe would never
 * reach the SF order — it is treated as no native support until that is
 * wired. Nexi and Svea have no equivalent at all.
 *
 * `reQuotesOnAddressChange` — the widget can ask us to re-price once the
 * shopper enters an address, so country-dependent rules can still be honoured
 * inside a single-step checkout.
 *
 * `nativePickup` — how many pickup locations the widget can express natively.
 * Klarna carries a single `delivery_details.pickup_location` per option, which
 * is workable for one location and a poor fit for a list of service points.
 */
export interface PaymentProviderShippingCapabilities {
  nativeOptions: boolean
  reQuotesOnAddressChange: boolean
  nativePickup: 'none' | 'single'
}

export const PROVIDER_SHIPPING_CAPABILITIES: Record<
  PaymentProviderName,
  PaymentProviderShippingCapabilities
> = {
  KLARNA: {
    nativeOptions: true,
    // The client-side `shipping_address_change` event is typed in
    // `KlarnaCheckout/types.ts`, but server-side re-quoting needs a merchant
    // callback URL on the order that we do not configure yet. Left false so
    // address-dependent rules split the checkout rather than silently pricing
    // freight off the store's own country.
    reQuotesOnAddressChange: false,
    nativePickup: 'single'
  },
  NEXI: { nativeOptions: false, reQuotesOnAddressChange: false, nativePickup: 'none' },
  SVEA: { nativeOptions: false, reQuotesOnAddressChange: false, nativePickup: 'none' },
  // Accepts `AvailableShippingMethods`, but no selection listener is wired —
  // see the note above.
  QLIRO: { nativeOptions: false, reQuotesOnAddressChange: false, nativePickup: 'none' },
  LEDYER: { nativeOptions: false, reQuotesOnAddressChange: false, nativePickup: 'none' },
  // Briqpay's `shipping` module collects a shipping *address*, not a rate — there is
  // no native shipping-method list and no selection callback. The `addressupdate`
  // event does expose zip and country, so address-conditioned rules could in
  // principle be re-quoted inside a single-step checkout; left false until that
  // handshake is wired, so those stores split rather than silently pricing freight
  // off the store's own country.
  BRIQPAY: { nativeOptions: false, reQuotesOnAddressChange: false, nativePickup: 'none' }
}

export type CheckoutTopologyReasonCode =
  /** The merchant pinned the topology in the store settings. */
  | 'FORCED_BY_CONFIG'
  /** More than one method is configured and the widget cannot list them. */
  | 'MULTIPLE_METHODS_UNSUPPORTED'
  /** A rule keys off the recipient country, which the widget learns too late. */
  | 'ADDRESS_DEPENDENT_CONDITIONS'
  /** Pickup is offered across more locations than the widget can express. */
  | 'MULTIPLE_PICKUP_LOCATIONS'

export interface CheckoutTopologyReason {
  code: CheckoutTopologyReasonCode
  /** The method that triggered the reason, when it is attributable to one. */
  methodId?: string
}

export interface CheckoutTopologyExplanation {
  topology: CheckoutTopology
  /** Empty for `NATIVE`; every reason that independently forces `SPLIT`. */
  reasons: CheckoutTopologyReason[]
}

export interface ResolveCheckoutTopologyParams {
  /** The *configured* methods, not the resolved ones — the answer has to be
   *  stable across contexts, otherwise the payment widget would remount when
   *  the shopper changes country. */
  methods?: ShippingMethod[] | null
  paymentProvider?: PaymentProviderName | null
  /** `store.pickups.length`. */
  pickupCount?: number
  preference?: CheckoutTopologyPreference | null
}

/**
 * Decide whether delivery is chosen before the payment widget (`SPLIT`) or
 * inside it (`NATIVE`), and say why.
 *
 * Derived from the configuration rather than left to a merchant toggle: a
 * merchant who adds a country rule to a natively-rendered checkout would
 * otherwise get freight priced off the store's own country and never see it.
 * `preference` exists only as an escape hatch.
 */
export const explainCheckoutTopology = ({
  methods,
  paymentProvider,
  pickupCount = 0,
  preference = 'AUTO'
}: ResolveCheckoutTopologyParams): CheckoutTopologyExplanation => {
  if (preference && preference !== 'AUTO') {
    return {
      topology: preference,
      reasons: preference === 'SPLIT' ? [{ code: 'FORCED_BY_CONFIG' }] : []
    }
  }

  const caps = PROVIDER_SHIPPING_CAPABILITIES[paymentProvider ?? 'KLARNA']
  const configured = methods ?? []
  const reasons: CheckoutTopologyReason[] = []

  if (configured.length > 1 && !caps.nativeOptions) {
    reasons.push({ code: 'MULTIPLE_METHODS_UNSUPPORTED' })
  }

  if (hasAddressDependentConditions(configured) && !caps.reQuotesOnAddressChange) {
    for (const method of configured) {
      if (hasAddressDependentConditions([method])) {
        reasons.push({ code: 'ADDRESS_DEPENDENT_CONDITIONS', methodId: method.id })
      }
    }
  }

  const offersPickup = configured.some((method) => method.kind === 'PICKUP')
  if (offersPickup && (caps.nativePickup === 'none' || pickupCount > 1)) {
    reasons.push({ code: 'MULTIPLE_PICKUP_LOCATIONS' })
  }

  return { topology: reasons.length ? 'SPLIT' : 'NATIVE', reasons }
}

export const resolveCheckoutTopology = (params: ResolveCheckoutTopologyParams): CheckoutTopology =>
  explainCheckoutTopology(params).topology

/** Convenience wrapper over a store's shipping config. */
export const explainCheckoutTopologyForConfig = (
  shippingConfig: ShippingProviderConfig | null | undefined,
  paymentProvider: PaymentProviderName | null | undefined,
  pickupCount = 0
): CheckoutTopologyExplanation =>
  explainCheckoutTopology({
    methods: shippingConfig?.defaultOptions?.methods,
    paymentProvider,
    pickupCount,
    preference: shippingConfig?.defaultOptions?.checkoutTopology
  })
